import { Box, IconButton, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map, { Layer, Marker, MapProvider, Source, useMap } from 'react-map-gl/mapbox';
import { ReactComponent as DetailAlertMIcon } from '~/assets/icons/detail-alert-m.svg';
import { ReactComponent as MarkerIcon } from '~/assets/icons/marker.svg';
import { CIRCLE_STEPS, EARTH_RADIUS_METERS } from '~/constants/map';
import alertApi from '~/apis/alert';
import axios from 'axios';
import {
  DEFAULT_GEOFENCE_COLOR,
  DEFAULT_GEOFENCE_FILL_OPACITY,
  DEFAULT_GEOFENCE_LINE_WIDTH,
  ALERT_MARKER_SIZE,
  DEFAULT_MAP_MARKER_ZOOM,
  DEFAULT_MAP_FIT_MAX_ZOOM,
  DEFAULT_MAP_FIT_PADDING,
} from '../contacts/mapConstants';

const MAP_STYLE = 'mapbox://styles/mapbox/streets-v12';

const isValidLongitude = (value: number): boolean => value >= -180 && value <= 180;
const isValidLatitude = (value: number): boolean => value >= -90 && value <= 90;

const toMapLngLat = (point: AlertLocationPoint | null): [number, number] | null => {
  if (!point) return null;
  const { lat, lng } = point;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (isValidLongitude(lng) && isValidLatitude(lat)) return [lng, lat];
  return null;
};

const closeLinearRing = (coordinates: [number, number][]) => {
  if (!coordinates.length) return coordinates;
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coordinates;
  return [...coordinates, first];
};

const createCircleRing = (center: AlertLocationPoint, radiusInMeters: number, steps = CIRCLE_STEPS) => {
  const normalizedCenter = toMapLngLat(center);
  if (!normalizedCenter || radiusInMeters <= 0) return [];

  const [centerLng, centerLat] = normalizedCenter;
  const latRad = (centerLat * Math.PI) / 180;

  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const bearing = (i / steps) * Math.PI * 2;
    const dx = radiusInMeters * Math.cos(bearing);
    const dy = radiusInMeters * Math.sin(bearing);

    const lng = centerLng + ((dx / (EARTH_RADIUS_METERS * Math.cos(latRad))) * 180) / Math.PI;
    const lat = centerLat + ((dy / EARTH_RADIUS_METERS) * 180) / Math.PI;

    ring.push([lng, lat]);
  }

  return closeLinearRing(ring);
};

type AlertLocationPoint = { lat: number | null; lng: number | null };

type AlertLocation = {
  type?: string | null;
  points?: AlertLocationPoint[] | null;
  radius?: number | null;
  color?: string | null;
};

type AlertApiResult = {
  violation_lat?: number | string | null;
  violation_lng?: number | string | null;
  location?: AlertLocation | null;
};

type AlertDetailResponse = {
  result?: AlertApiResult | null;
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const MapResizeTrigger = ({ isFullscreen }: { isFullscreen: boolean }) => {
  const { current: map } = useMap();

  useEffect(() => {
    if (map) {
      map.resize();
      const timers = [100, 300, 500].map((ms) => setTimeout(() => map.resize(), ms));
      return () => timers.forEach(clearTimeout);
    }
  }, [isFullscreen, map]);

  return null;
};

const AlertDetailMap = ({ alertId }: { alertId?: string | number | null }) => {
  const { t } = useTranslation('admin/alert');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [locationData, setLocationData] = useState<AlertApiResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!alertId) {
      setLocationData(null);
      return;
    }

    const fetchLocation = async () => {
      setIsLoading(true);
      setLocationData(null);

      try {
        const response = await alertApi.getAlertDetail(String(alertId));
        const payload = response.data as AlertDetailResponse;
        const detail = payload.result ?? null;
        const lat = parseNumber(detail?.violation_lat);
        const lng = parseNumber(detail?.violation_lng);

        if (lat === null || lng === null) {
          return;
        }

        setLocationData({
          ...detail,
          violation_lat: lat,
          violation_lng: lng,
          location: detail?.location ?? null,
        });
      } catch (error: unknown) {
        if (axios.isCancel(error)) return;
      } finally {
        setIsLoading(false);
      }
    };

    void fetchLocation();
  }, [alertId]);

  const violationCoordinates = useMemo(() => {
    if (!locationData) return null;
    const lat = parseNumber(locationData.violation_lat);
    const lng = parseNumber(locationData.violation_lng);
    return lat !== null && lng !== null ? ([lng, lat] as [number, number]) : null;
  }, [locationData]);

  const geofenceGeoJson = useMemo(() => {
    if (!locationData?.location) return null;
    const color = locationData.location.color || DEFAULT_GEOFENCE_COLOR;

    if (locationData.location.type === 'circle') {
      const centerPoint = locationData.location.points?.[0] ?? null;
      const radiusInMeters = typeof locationData.location.radius === 'number' ? locationData.location.radius : null;
      if (!centerPoint || radiusInMeters === null) return null;
      const ring = createCircleRing(centerPoint, radiusInMeters);
      if (ring.length < 4) return null;
      return {
        type: 'FeatureCollection' as const,
        features: [
          {
            type: 'Feature' as const,
            properties: { color },
            geometry: {
              type: 'Polygon' as const,
              coordinates: [ring],
            },
          },
        ],
      };
    }

    const points =
      locationData.location.points?.map(toMapLngLat).filter((p): p is [number, number] => p !== null) ?? [];
    if (points.length < 3) return null;
    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: { color },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [closeLinearRing(points)],
          },
        },
      ],
    };
  }, [locationData]);

  const { current: map } = useMap();

  useEffect(() => {
    if (!map || !violationCoordinates) return;

    const bounds: [[number, number], [number, number]] = [
      [violationCoordinates[0], violationCoordinates[1]],
      [violationCoordinates[0], violationCoordinates[1]],
    ];

    if (geofenceGeoJson?.features?.[0]?.geometry?.coordinates?.[0]) {
      const polygon = geofenceGeoJson.features[0].geometry.coordinates[0];
      for (const [lng, lat] of polygon) {
        bounds[0][0] = Math.min(bounds[0][0], lng);
        bounds[0][1] = Math.min(bounds[0][1], lat);
        bounds[1][0] = Math.max(bounds[1][0], lng);
        bounds[1][1] = Math.max(bounds[1][1], lat);
      }
    }

    if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) {
      map.flyTo({ center: violationCoordinates, zoom: DEFAULT_MAP_MARKER_ZOOM });
      return;
    }

    map.fitBounds(bounds, {
      padding: DEFAULT_MAP_FIT_PADDING,
      duration: 500,
      maxZoom: DEFAULT_MAP_FIT_MAX_ZOOM,
    });
  }, [map, violationCoordinates, geofenceGeoJson]);

  const handleToggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await containerRef.current?.requestFullscreen();
      }
    } catch {
      // ignore fullscreen errors
    }
  };

  if (isLoading) {
    return (
      <Box className='flex h-[146px] w-full items-center justify-center rounded-[4px] bg-[#f0f0f0]'>
        <Typography className='text-sm text-[#404854]'>Loading map…</Typography>
      </Box>
    );
  }

  if (!violationCoordinates) {
    return (
      <Box className='flex h-[146px] w-full items-center justify-center rounded-[4px] bg-[#f0f0f0]'>
        <Typography className='text-sm text-[#404854]'>{t('alertDetail.noLocationData')}</Typography>
      </Box>
    );
  }

  const geoColor = locationData?.location?.color ?? DEFAULT_GEOFENCE_COLOR;

  return (
    <MapProvider>
      <Box
        ref={containerRef}
        className={`relative w-full overflow-hidden rounded-[4px] bg-[#f0f0f0] ${!isFullscreen ? 'transition-[height] duration-300' : ''}`}
        style={{ height: isFullscreen ? '100vh' : '246px' }}
      >
        <Map
          id='detailMap'
          mapboxAccessToken={process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}
          initialViewState={{
            longitude: violationCoordinates[0],
            latitude: violationCoordinates[1],
            zoom: DEFAULT_MAP_MARKER_ZOOM,
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle={MAP_STYLE}
          attributionControl={false}
          trackResize={true}
        >
          <MapResizeTrigger isFullscreen={isFullscreen} />

          {geofenceGeoJson && (
            <Source id='alert-geofence' type='geojson' data={geofenceGeoJson}>
              <Layer
                id='alert-geofence-fill'
                type='fill'
                paint={{
                  'fill-color': geoColor,
                  'fill-opacity': DEFAULT_GEOFENCE_FILL_OPACITY,
                }}
              />
              <Layer
                id='alert-geofence-line'
                type='line'
                paint={{
                  'line-color': geoColor,
                  'line-width': DEFAULT_GEOFENCE_LINE_WIDTH,
                }}
              />
            </Source>
          )}

          <Marker longitude={violationCoordinates[0]} latitude={violationCoordinates[1]} anchor='center'>
            <div
              style={{
                width: ALERT_MARKER_SIZE,
                height: ALERT_MARKER_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MarkerIcon width={ALERT_MARKER_SIZE} height={ALERT_MARKER_SIZE} />
            </div>
          </Marker>
        </Map>

        <IconButton
          aria-label={isFullscreen ? t('alertDetail.exitFullscreenMap') : t('alertDetail.enterFullscreenMap')}
          onClick={handleToggleFullscreen}
          className='absolute bottom-3 right-3 z-10 h-9 w-9 rounded-lg bg-[#16A34A] text-white shadow-lg hover:bg-[#15803D] active:scale-95 transition-transform'
        >
          <DetailAlertMIcon className='h-5 w-5 brightness-0 invert' />
        </IconButton>
      </Box>
    </MapProvider>
  );
};

export default memo(AlertDetailMap);
